import React, { useCallback, useMemo, useRef } from 'react'
import { VariableSizeList as List } from 'react-window'
import AutoSizer from 'react-virtualized-auto-sizer'
import InfiniteLoader from 'react-window-infinite-loader'
import { Layer } from '../variable/spaceTime.types'
import Item from './viewSelectModalVariable.scrollItem'
import { Expression } from '~/prix/types/expression'
import { ShapeBase } from '~/prix'
import { VariableWithConclusion } from '../variable/variables.typing'

interface InfiniteScrollProps {
  variables: Array<VariableWithConclusion>
  currentBaseLayer: Layer
  handleSelectVariable: (variable: VariableWithConclusion) => void
  getCollapsedOpen: (isOpen: boolean, key: string | undefined) => void
  collapsedStates: { [key: string]: boolean }
  setIndexes: React.Dispatch<React.SetStateAction<{ startIndex: number; endIndex: number }>>
  nextPages: number
  apiIsLoading: boolean
  setIsLoadingMore: React.Dispatch<React.SetStateAction<boolean>>
  getFilter: (expression: Record<number, Expression> | undefined, index: string) => void
  filters: Expression[] | undefined
  selectedVariableIsFilterableBy: ShapeBase | undefined
}

function InfiniteScroll({
  variables,
  currentBaseLayer,
  handleSelectVariable,
  getCollapsedOpen,
  collapsedStates,
  setIndexes,
  nextPages,
  apiIsLoading,
  setIsLoadingMore,
  getFilter,
  filters,
  selectedVariableIsFilterableBy,
}: InfiniteScrollProps) {
  const hasNextPages = nextPages > 0
  const itemCount = !apiIsLoading && hasNextPages ? variables.length + 1 : variables.length
  const isItemLoaded = useCallback(
    (index: number) => {
      return index < variables.length
    },
    [variables.length],
  )
  const listRef = useRef<List>(null)
  const initialSize = 90
  const sizes = useMemo(
    () => new Array(variables.length).fill(initialSize),
    [variables.length, initialSize],
  )

  const getItemSize = (index: number) => {
    return sizes[index]
  }
  const loadMoreItems = useCallback(
    (startIndex: number) => {
      setIndexes({ startIndex, endIndex: startIndex + 50 })
      setIsLoadingMore(true)
    },
    [setIndexes, setIsLoadingMore],
  )

  return (
    <AutoSizer>
      {({ height, width }) => (
        <InfiniteLoader
          isItemLoaded={isItemLoaded}
          itemCount={itemCount}
          loadMoreItems={loadMoreItems}
          threshold={0}
        >
          {({ onItemsRendered, ref }) => (
            <List
              height={height}
              itemCount={itemCount}
              itemSize={getItemSize}
              width={width}
              onItemsRendered={onItemsRendered}
              ref={listRef}
            >
              {({ index, style }) => (
                <Item
                  index={index}
                  style={style}
                  isItemLoaded={isItemLoaded}
                  variables={variables}
                  currentBaseLayer={currentBaseLayer}
                  handleSelectVariable={handleSelectVariable}
                  collapsedStates={collapsedStates}
                  listRef={listRef}
                  sizes={sizes}
                  getCollapsedOpen={getCollapsedOpen}
                  selectedVariableIsFilterableBy={selectedVariableIsFilterableBy}
                  isApiLoading={apiIsLoading}
                  getFilter={getFilter}
                  filters={filters}
                />
              )}
            </List>
          )}
        </InfiniteLoader>
      )}
    </AutoSizer>
  )
}

export default InfiniteScroll
